import { ArrowLeft, CheckSquare, Heart, Sparkles, Calendar, Star, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function HabitSelectionGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-emerald-100 rounded-full">
              <CheckSquare className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Habit Selection</h1>
              <p className="text-emerald-600">Week 12, Day 5 - Keeping Your Glow</p>
            </div>
          </div>

          <div className="prose prose-emerald max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Review all the practices from your 12 weeks. Circle the 5-7 that made the biggest difference. 
              Create a simple weekly schedule that includes these non-negotiable habits.
            </p>

            <div className="bg-emerald-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why This Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Explicit planning increases habit maintenance by 60%</li>
                <li>• Identifying your core practices ensures transformation becomes lifestyle</li>
                <li>• You can't maintain everything - choosing is essential</li>
                <li>• Non-negotiables protect what matters most</li>
                <li>• A simple schedule removes daily decision fatigue</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Review Your 12 Weeks</h2>
            
            <p className="text-gray-700 mb-4">Think through each week's focus. Which practices had the most impact?</p>
            
            <div className="grid md:grid-cols-2 gap-3 mb-8">
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 1:</span> Foundation & Mindset
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 2:</span> Nutrition Essentials
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 3:</span> Skincare Rituals
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 4:</span> Movement & Strength
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 5:</span> Sleep & Recovery
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 6:</span> Stress Management
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 7:</span> Confidence & Style
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 8:</span> Career Empowerment
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 9:</span> Alcohol Reduction
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 10:</span> Social Connections
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 11:</span> Creative Expression
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <span className="font-semibold text-gray-700">Week 12:</span> Keeping Your Glow
              </div>
            </div>

            <div className="bg-gradient-to-r from-emerald-100 to-teal-100 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-emerald-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5" />
                Sample Non-Negotiable Habits
              </h2>
              <p className="text-gray-700 mb-4">Choose 5-7 from practices like:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Morning meditation (10 minutes)</li>
                <li>• Evening journaling</li>
                <li>• Exercise 4x per week</li>
                <li>• Skincare AM/PM routine</li>
                <li>• 8 glasses of water daily</li>
                <li>• Screen-free hour before bed</li>
                <li>• Weekly connection with friend</li>
                <li>• Alcohol-free days</li>
                <li>• Gratitude practice</li>
                <li>• Creative activity weekly</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Create Your Weekly Schedule</h2>
            
            <div className="bg-white border-2 border-emerald-200 rounded-xl p-5 mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-gray-900">Template</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-700">DAILY (Morning)</p>
                  <p className="text-gray-500 text-sm">___________________</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-700">DAILY (Evening)</p>
                  <p className="text-gray-500 text-sm">___________________</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-700">3-4x PER WEEK</p>
                  <p className="text-gray-500 text-sm">___________________</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="font-medium text-gray-700">WEEKLY</p>
                  <p className="text-gray-500 text-sm">___________________</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Tips for Maintaining Habits
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Start small - 5 minutes is better than skipping</li>
                <li>• Stack habits together (meditation right after coffee)</li>
                <li>• Use reminders and alarms initially</li>
                <li>• Track your habits visually</li>
                <li>• Plan for obstacles - what if you travel? Get sick?</li>
                <li>• Celebrate consistency, not perfection</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
